/**
 * =============================================================================
 * Keycloak Configuration
 * =============================================================================
 *
 * Keycloak client configuration and initialization.
 *
 * This module:
 * - Initializes Keycloak client with proper settings
 * - Handles token refresh
 * - Provides authentication state
 *
 * Environment Variables (loaded from .env):
 * - VITE_KEYCLOAK_URL: Keycloak server URL
 * - VITE_KEYCLOAK_REALM: Keycloak realm name
 * - VITE_KEYCLOAK_CLIENT_ID: Frontend client ID
 */

import Keycloak from 'keycloak-js'
import {
  readKeycloakTokens,
  saveKeycloakTokens,
  clearKeycloakTokens,
  markSessionEnded,
  clearSessionEnded,
} from './sessionStore'

// =============================================================================
// Configuration
// =============================================================================

/**
 * Keycloak configuration from runtime config first, then build-time env.
 *
 * Priority:
 * 1) window.__APP_CONFIG__ (container runtime; preferred for prod/test)
 * 2) import.meta.env (local dev / fallback)
 */
const runtimeConfig = window.__APP_CONFIG__ || {}

const keycloakConfig = {
  url: runtimeConfig.KEYCLOAK_URL || import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: runtimeConfig.KEYCLOAK_REALM || import.meta.env.VITE_KEYCLOAK_REALM || 'rehapiano',
  clientId: runtimeConfig.KEYCLOAK_CLIENT_ID || import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'admin-portal',
}

// Warn if using fallback values in non-development environment
if (import.meta.env.PROD && !runtimeConfig.KEYCLOAK_URL && !import.meta.env.VITE_KEYCLOAK_URL) {
  console.warn('[Keycloak] Runtime KEYCLOAK_URL not set! Using fallback value.')
}

// =============================================================================
// Keycloak Instance
// =============================================================================

/**
 * Keycloak client instance.
 * Configured for the admin-portal frontend client.
 */
const keycloak = new Keycloak(keycloakConfig)

export default keycloak
// =============================================================================
// Keycloak Initialization
// =============================================================================

let initPromise: Promise<boolean> | null = null;

/**
 * Initialize Keycloak and restore an existing session (FE issue #24).
 *
 * Before this fix `init()` was called with neither `onLoad` nor stored tokens,
 * so keycloak-js never attempted a restore: after F5 `keycloak.authenticated`
 * was `false`, every request went out without a bearer and the portal showed
 * the login page. A reload looked like a forced logout.
 *
 * Two restore paths, tried in this order by keycloak-js itself:
 *  1. `token`/`refreshToken` this tab stored on its last successful auth —
 *     keycloak-js immediately refreshes them (`updateToken(-1)`), so a dead
 *     refresh token cannot resurrect a stale session,
 *  2. if that fails, `onLoad: 'check-sso'` performs a *silent* iframe check
 *     against Keycloak (public/silent-check-sso.html) and picks up an SSO
 *     session started elsewhere. It never redirects to the login form, so an
 *     anonymous visitor still lands on our own login page.
 */
export const initKeycloak = (onAuthenticatedCallback: () => void) => {
  if (!initPromise) {
    const stored = readKeycloakTokens();
    initPromise = keycloak.init({
      // Never force a redirect to the Keycloak login form on app load; the
      // user must still explicitly choose Keycloak login. check-sso only
      // *checks*, silently, in an iframe.
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,
      pkceMethod: "S256",
      ...(stored
        ? { token: stored.token, refreshToken: stored.refreshToken, idToken: stored.idToken }
        : {}),
    });
  }

  initPromise
    .then((authenticated) => {
      if (authenticated) {
        persistTokens();
      } else {
        // Stored tokens could not be refreshed and there is no SSO session:
        // the session really is over. Tell the login page why.
        if (readKeycloakTokens()) {
          clearKeycloakTokens();
          markSessionEnded("restore_failed");
        }
      }
      onAuthenticatedCallback();
    })
    .catch((error) => {
      console.error("Keycloak initialization failed:", error);
      clearKeycloakTokens();
      onAuthenticatedCallback();
    });
};

/** Mirror the live keycloak-js tokens into sessionStorage (see sessionStore). */
export const persistTokens = (): void => {
  saveKeycloakTokens({
    token: keycloak.token,
    refreshToken: keycloak.refreshToken,
    idToken: keycloak.idToken,
  });
};
// =============================================================================
// Helper Functions
// =============================================================================

// /**
//  * Initialize Keycloak and check authentication status.
//  *
//  * @param onAuthenticatedCallback - Callback when auth check completes
//  *
//  * Configuration:
//  * - onLoad: 'check-sso' - Silently check if user is logged in
//  * - silentCheckSsoRedirectUri - Use iframe for silent SSO check
//  * - pkceMethod: 'S256' - Use PKCE for enhanced security
//  */
// export const initKeycloak = (onAuthenticatedCallback: () => void) => {
//   keycloak
//     .init({
//       onLoad: 'check-sso',
//       silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
//       pkceMethod: 'S256',
//     })
//     .then((authenticated) => {
//       if (authenticated) {
//         console.log('User is authenticated')
//       } else {
//         console.log('User is not authenticated')
//       }
//       onAuthenticatedCallback()
//     })
//     .catch((error) => {
//       console.error('Keycloak initialization failed:', error)
//       onAuthenticatedCallback()
//     })
// }

/**
 * Get current access token.
 * Returns undefined if not authenticated.
 */
export const getToken = (): string | undefined => {
  return keycloak.token
}

/**
 * Check if user is authenticated.
 */
export const isAuthenticated = (): boolean => {
  return !!keycloak.authenticated
}

/**
 * Get current user's roles.
 * Returns realm roles assigned to the user.
 */
export const getUserRoles = (): string[] => {
  return keycloak.realmAccess?.roles || []
}

/**
 * Check if user has a specific role.
 *
 * @param role - Role to check
 * @returns True if user has the role
 */
export const hasRole = (role: string): boolean => {
  return getUserRoles().includes(role)
}

/**
 * Get user profile information.
 *
 * Extracts user data from the Keycloak token including:
 * - Standard OIDC claims (sub, email, given_name, family_name)
 * - Custom institution_id claim (requires protocol mapper in Keycloak)
 *
 * Note: institution_id must be configured in Keycloak as:
 * 1. User attribute: institution_id
 * 2. Protocol mapper: User Attribute → Token Claim
 */
export const getUserInfo = () => {
  if (!keycloak.tokenParsed) return null

  // Type assertion for custom claims
  const tokenParsed = keycloak.tokenParsed as {
    sub?: string
    preferred_username?: string
    email?: string
    given_name?: string
    family_name?: string
    institution_id?: string
  }

  return {
    id: tokenParsed.sub,
    username: tokenParsed.preferred_username,
    email: tokenParsed.email,
    firstName: tokenParsed.given_name,
    lastName: tokenParsed.family_name,
    institutionId: tokenParsed.institution_id,
    roles: getUserRoles(),
  }
}

/**
 * Refresh access token.
 * Called periodically to keep session alive.
 *
 * @param minValidity - Minimum seconds of validity required
 * @returns Promise resolving to refreshed status
 */
export const refreshToken = async (minValidity = 30): Promise<boolean> => {
  try {
    const refreshed = await keycloak.updateToken(minValidity)
    if (refreshed) {
      // Keep the restore copy in step with the live tokens (issue #24),
      // otherwise a reload would replay an already-rotated refresh token.
      persistTokens()
    }
    return refreshed
  } catch (error) {
    console.error('Failed to refresh token:', error)
    clearKeycloakTokens()
    return false
  }
}

/**
 * Initiate login flow.
 * Redirects to Keycloak login page.
 */
export const login = () => {
  keycloak.login()
}

/**
 * Initiate logout flow.
 * Clears session and redirects to Keycloak logout.
 */
export const logout = () => {
  // An explicit sign-out is not an expiry: drop the restore copy and the
  // "session expired" flag so the login page stays quiet (issue #24).
  clearKeycloakTokens()
  clearSessionEnded()
  keycloak.logout({
    redirectUri: window.location.origin,
  })
}
